import _merge from 'lodash/merge';
import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import _get from 'lodash/get';
import { Lightning } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { getLayoutTheme } from '../../../tornado/utils/theme';
import { SELECTED_SIGNAL } from '../../../tornado/signals';
import { PAGE_STATE, PLACEHOLDER } from '../../../tornado/utils/virtualization';
import { sendEvent } from '../../../../logic/analytics/analytics.utils';
import { fetchSideGlassPage } from '../../../../logic/player/player.actions';
import { getTargetData } from '../../../cta/cta.utils';
import {
  getInitialStyle,
  defaultContext,
  getUnFocusedStyle,
  getFocusedStyle,
  getContentWidth,
  PADDING_TOP,
  PADDING_BOTTOM,
  CONTENT_GAP,
  Z_INDEX_FOCUSED,
  Z_INDEX_UNFOCUSED,
  HEIGHT,
} from './sideGlass.style';
import { CLOSE_SIGNAL, TEMPLATES } from './sideGlass.constants';
import { getContainerPositions, getPagesToRequest } from './sideGlass.utils';
import connect from './sideGlass.connector';

export class SideGlass extends Lightning.Component {
  static _template() {
    return _merge(
      {
        rect: true,
        BackgroundOverlay: {
          rect: true,
        },
        Container: {},
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
    this._index = 0;
    this._maxIndex = 0;
    this._focused = null;
    this._isLiveSideGlass = false;
    this._blockId = '';
    this._itemTemplate = '';
    this._firstItem = undefined;
    this._itemsCount = 0;
    this._itemsPerPage = 0;
    this._totalItems = 0;
    this._lookahead = 0;
    this._pages = {};
    this._positions = [];
  }

  _detach() {
    this._focused = null;
    if (this._unsubscribe) {
      this._unsubscribe();
    }
  }

  _active() {
    if (!this._unsubscribe) {
      this._unsubscribe = connect(this);
    }
  }

  set data(value) {
    if (!value) {
      return;
    }

    let itemTemplate = _get(value, 'content.contentTemplateId');
    let template = TEMPLATES[itemTemplate];
    if (!template) {
      itemTemplate = Object.keys(TEMPLATES)[0];
      template = TEMPLATES[itemTemplate];
    }

    const { screenSize } = this._style;
    const items = _get(value, 'content.items', []);
    const pagination = _get(value, 'content.pagination') || {};
    const blockId = _get(value, 'id', '');
    // NOTE: the very same list just got paginated, so the existing views could be reused.
    // The first item is compared as well, as the block identifiers are not unique across the layouts
    const isBlockUpdate =
      blockId === this._blockId && itemTemplate === this._itemTemplate && items[0] === this._firstItem;

    const itemSize = {
      ...template.Sizes[itemTemplate][RENDERING_CONTEXT.GENERAL][screenSize],
      w: getContentWidth(itemTemplate, screenSize),
    };
    const blockTheme = getLayoutTheme(_get(value, 'theme'));
    const contentGap = CONTENT_GAP[screenSize];
    const panelHeight = HEIGHT[screenSize];

    this._blockId = blockId;
    this._itemTemplate = itemTemplate;
    this._firstItem = items[0];
    this._itemType = template;
    this._itemSize = itemSize;
    this._contentGap = contentGap;
    this._colorScheme = blockTheme.colorScheme;
    this._itemsCount = items.length;
    this._itemsPerPage = pagination.itemsPerPage || 0;
    this._totalItems = Math.max(pagination.totalItems || 0, items.length);
    this._pages = value.pages || {};
    this._maxIndex = Math.max(items.length - 1, 0);
    this._lookahead = Math.ceil(panelHeight / (itemSize.h + contentGap));
    this._positions = getContainerPositions({
      itemsCount: items.length,
      itemHeight: itemSize.h,
      contentGap,
      panelHeight,
      paddingTop: PADDING_TOP[screenSize],
      paddingBottom: PADDING_BOTTOM[screenSize],
    });

    const container = this.tag('Container');
    if (isBlockUpdate) {
      items.forEach((content, index) => {
        const child = container.children[index];

        if (child) {
          child.item = this._makeItemData(content);
        } else {
          container.childList.a(this._makeChildData(content, index));
        }
      });

      // NOTE: the list could shrink in case the block holds less items than it was announced
      while (container.children.length > items.length) {
        container.childList.remove(container.children[container.children.length - 1]);
      }
    } else {
      container.children = items.map((content, index) => this._makeChildData(content, index));
      this._index = 0;
    }

    this.index = Math.min(this._index, this._maxIndex);
    this._refocus();
  }

  get index() {
    return this._index;
  }

  set index(value) {
    this._index = value;
    const container = this.tag('Container');
    const child = container.children[this._index];

    if (child) {
      // NOTE: in case if items could overlap, it's necessary to raise a focused item's z-index so it will be render "on top"
      if (Z_INDEX_FOCUSED !== Z_INDEX_UNFOCUSED) {
        if (this._focused) {
          this._focused.zIndex = Z_INDEX_UNFOCUSED;
        }
        child.zIndex = Z_INDEX_FOCUSED;
      }
      container.setSmooth('y', this._positions[this._index]);
    }
    this._focused = child;

    this._requestPages();
  }

  set isLiveSideGlass(sideGlassFlag) {
    this._isLiveSideGlass = sideGlassFlag;
  }

  get isLiveSideGlass() {
    return this._isLiveSideGlass;
  }

  _makeItemData(content) {
    return {
      content,
      config: this._itemSize,
      template: this._itemTemplate,
      colorScheme: this._colorScheme,
      sideGlass: true,
    };
  }

  _makeChildData(content, index) {
    return {
      ...this._itemSize,
      type: this._itemType,
      y: index * (this._itemSize.h + this._contentGap),
      item: this._makeItemData(content),
      signals: {
        [SELECTED_SIGNAL]: '_handleSelected',
      },
    };
  }

  /**
   * Requests the block pages holding the items which are about to be displayed.
   * The placeholders of a requested page are added to the player state synchronously,
   * so the panel could be scrolled further while the items are being loaded
   */
  _requestPages() {
    if (!this._blockId) {
      return;
    }

    const pages = getPagesToRequest({
      index: this._index,
      lookahead: this._lookahead,
      itemsPerPage: this._itemsPerPage,
      totalItems: this._totalItems,
      pages: this._pages,
    });

    pages.forEach(page => {
      this._pages = { ...this._pages, [page]: PAGE_STATE.LOADING };

      Store.dispatch(fetchSideGlassPage(page));
    });
  }

  _handleSelected(item) {
    if (!item || item === PLACEHOLDER) {
      return;
    }

    const { id } = getTargetData(item);

    sendEvent({ key: 'navigationPanelReplay', clipId: id });
    this.signal(SELECTED_SIGNAL, item);
  }

  _unfocus() {
    this.patch(getUnFocusedStyle(this._style.screenSize));
  }

  _focus() {
    this.patch(getFocusedStyle(this._style.screenSize));
  }

  _getFocused() {
    return this.tag('Container').children[this._index];
  }

  _handleUp() {
    if (this._index > 0) {
      this.index -= 1;
    }

    return true;
  }

  _handleDown() {
    if (this._index < this._maxIndex) {
      this.index += 1;
    }

    return true;
  }

  _fireCloseSignal() {
    this.signal(CLOSE_SIGNAL);

    return true;
  }

  _handleBack() {
    return this._fireCloseSignal();
  }

  _handleLeft() {
    return this._fireCloseSignal();
  }
}
